import { FC, Fragment, useMemo, useState } from 'react';
import { Alert, Card, Col, Form, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import caresLogoWide from '../../assets/images/cares/logo-wide.png';
import authCover from '../../assets/images/cares/auth-cover.png';
import { getCaresShellBootstrap } from './shell/bootstrap';
import './cares-authentication.scss';

const CaresForgotPassword: FC = () => {
  const csrfToken = useMemo(() => document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '', []);
  const bootstrap = getCaresShellBootstrap();
  const validationMessages = Object.values(bootstrap.validationErrors ?? {}).flat();
  const emailError = bootstrap.validationErrors?.email?.[0];
  const emailErrorId = emailError ? 'cares-forgot-password-email-error' : undefined;
  const [email, setEmail] = useState(() => bootstrap.authPage.email ?? '');

  return (
    <Fragment>
      <Row className="authentication cares-authentication mx-0 bg-white">
        <Col xxl={5} xl={5} lg={12}>
          <Row className="justify-content-center align-items-center h-100">
            <Col xxl={6} xl={9} lg={6} md={6} sm={8} className="col-12">
              <Card className="custom-card shadow-none my-4">
                <Card.Body className="p-4">
                  <div className="mb-3 d-flex justify-content-center">
                    <Link to="/">
                      <img src={caresLogoWide} alt="CARES logo" className="authentication-brand desktop-logo" />
                      <img src={caresLogoWide} alt="CARES logo" className="authentication-brand desktop-dark" />
                    </Link>
                  </div>
                  <p className="h5 mb-2 text-center">Forgot your password?</p>
                  <p className="mb-4 text-muted op-7 fw-normal text-center">
                    Enter your CARES account email address and we&apos;ll send a reset link.
                  </p>

                  {bootstrap.status ? <Alert variant="success" role="status">{bootstrap.status}</Alert> : null}
                  {validationMessages.length > 0 ? (
                    <Alert variant="danger" role="alert">
                      {validationMessages.map((message, index) => (
                        <div key={`${message}-${index}`}>{message}</div>
                      ))}
                    </Alert>
                  ) : null}

                  <Form method="post" action="/forgot-password">
                    <input type="hidden" name="_token" value={csrfToken} />
                    <Row className="gy-3">
                      <Col xl={12}>
                        <Form.Label htmlFor="cares-forgot-password-email" className="text-default">
                          Email
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          size="lg"
                          id="cares-forgot-password-email"
                          placeholder="you@example.com"
                          autoComplete="email"
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                          isInvalid={Boolean(emailError)}
                          aria-describedby={emailErrorId}
                          required
                          autoFocus
                        />
                        {emailError ? <Form.Control.Feedback id={emailErrorId} type="invalid" className="d-block">{emailError}</Form.Control.Feedback> : null}
                      </Col>
                    </Row>

                    <div className="d-grid mt-4">
                      <button type="submit" className="btn btn-lg btn-primary">
                        Email Password Reset Link
                      </button>
                    </div>
                  </Form>

                  <div className="text-center">
                    <p className="text-muted mt-3 mb-0">
                      Remembered it? <a href="/login" className="text-primary">Back to Sign In</a>
                    </p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
        <Col xxl={7} xl={7} lg={7} className="d-xl-block d-none px-0">
          <div className="authentication-cover bg-primary">
            <div>
              <div className="authentication-cover-image">
                <img src={authCover} alt="CARES clinical scheduling workspace" />
              </div>
              <div className="text-center mb-4">
                <h1 className="text-fixed-white">Password Recovery</h1>
                <p className="text-fixed-white op-8 mb-0">
                  Request a secure reset link for your CARES account.
                </p>
              </div>
              <div className="btn-list text-center">
                <a href="/login" className="btn btn-icon authentication-cover-icon btn-wave" aria-label="Go to CARES sign in">
                  <i className="ri-login-box-line fw-bold"></i>
                </a>
                <Link to="/" className="btn btn-icon authentication-cover-icon btn-wave" aria-label="Go to CARES home">
                  <i className="ri-home-4-line fw-bold"></i>
                </Link>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Fragment>
  );
};

export default CaresForgotPassword;
